import { useLayoutEffect, useRef } from 'react';

// Touch layouts keep scrolling entirely native. Observers only toggle the
// compact title and reveal sections; no scroll handler redraws mobile artwork.
export function useProductDetailMotion(onCompact: (compact: boolean) => void) {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const purchaseRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const scroll = scrollRef.current;
    const hero = heroRef.current;
    const title = titleRef.current;
    const purchase = purchaseRef.current;
    if (!root || !scroll || !hero || !title || !purchase) return;
    const artwork = hero.querySelector<HTMLElement>('.pd-hero-motion')!;
    const atmosphere = root.querySelector<HTMLElement>('.pd-atmosphere')!;
    const toolbar = root.querySelector<HTMLElement>('.pd-toolbar')!;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const touchLayout = window.matchMedia('(max-width: 850px), (pointer: coarse)');
    const sections = [...root.querySelectorAll<HTMLElement>('.pd-reveal')];
    let frame = 0;
    let measureFrame = 0;
    let compact = false;
    let disposed = false;
    let desktopHeight = root.clientHeight;
    let observedToolbarHeight = -1;
    let titleObserver: IntersectionObserver | undefined;
    let revealObserver: IntersectionObserver | undefined;

    const updateCompact = (next: boolean) => {
      if (next !== compact) {
        compact = next;
        onCompact(next);
      }
    };
    const renderDesktop = () => {
      frame = 0;
      const y = scroll.scrollTop;
      const progress = Math.min(1, Math.max(0, (y - 24) / (desktopHeight * 0.7)));
      const eased = progress * progress * (3 - 2 * progress);
      // Set styles only on the two animated layers, never inherited variables
      // on the entire dialog. Layout measurements stay outside this callback.
      artwork.style.transform = `translate3d(0, ${-Math.min(y * 0.075, 68)}px, 0) scale(${1 + eased * 0.035})`;
      artwork.style.filter = `blur(${eased * 12}px)`;
      artwork.style.opacity = `${1 - eased * 0.76}`;
      atmosphere.style.opacity = `${eased * 0.16}`;
    };
    const scheduleDesktop = () => {
      if (!frame) frame = window.requestAnimationFrame(renderDesktop);
    };
    const resetArtwork = () => {
      artwork.style.removeProperty('transform');
      artwork.style.removeProperty('filter');
      artwork.style.removeProperty('opacity');
      atmosphere.style.removeProperty('opacity');
    };

    const observeTitle = () => {
      const top = Math.round(toolbar.getBoundingClientRect().height);
      if (top === observedToolbarHeight && titleObserver) return;
      observedToolbarHeight = top;
      titleObserver?.disconnect();
      titleObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry)
            updateCompact(entry.boundingClientRect.bottom < (entry.rootBounds?.top ?? top));
        },
        { root: scroll, rootMargin: `-${top}px 0px 0px 0px`, threshold: 0 }
      );
      titleObserver.observe(title);
    };
    const reveal = (element: HTMLElement) => {
      element.classList.add('is-revealed');
      revealObserver?.unobserve(element);
    };
    const setupReveals = () => {
      revealObserver?.disconnect();
      if (motion.matches) {
        delete root.dataset.revealReady;
        return;
      }
      root.dataset.revealReady = 'true';
      // Mobile sections start appearing before they enter the viewport; a fast
      // swipe must never land on a blank card waiting for a long entrance.
      revealObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries)
            if (entry.isIntersecting) reveal(entry.target as HTMLElement);
        },
        {
          root: scroll,
          rootMargin: touchLayout.matches ? '0px 0px 140px 0px' : '-76px 0px -106px 0px',
          threshold: 0,
        }
      );
      for (const section of sections)
        if (!section.classList.contains('is-revealed')) revealObserver.observe(section);
    };
    const configureMotion = () => {
      scroll.removeEventListener('scroll', scheduleDesktop);
      window.cancelAnimationFrame(frame);
      frame = 0;
      resetArtwork();
      if (!touchLayout.matches && !motion.matches) {
        scroll.addEventListener('scroll', scheduleDesktop, { passive: true });
        renderDesktop();
      }
      setupReveals();
    };
    const measure = () => {
      measureFrame = 0;
      // The hero uses stable large-viewport CSS units. Opening/closing the
      // browser address bar cannot resize the picture or shift the copy.
      const reserve = `${Math.ceil(purchase.getBoundingClientRect().height)}px`;
      if (root.style.getPropertyValue('--pd-purchase-space') !== reserve)
        root.style.setProperty('--pd-purchase-space', reserve);
      desktopHeight = root.clientHeight;
      observeTitle();
      if (!touchLayout.matches && !motion.matches) scheduleDesktop();
    };
    const scheduleMeasure = () => {
      if (!measureFrame) measureFrame = window.requestAnimationFrame(measure);
    };
    const onFocus = (event: FocusEvent) => {
      const section = (event.target as HTMLElement).closest<HTMLElement>('.pd-reveal');
      if (section) reveal(section);
    };
    const resize = new ResizeObserver(scheduleMeasure);
    resize.observe(purchase);
    resize.observe(toolbar);
    root.addEventListener('focusin', onFocus);
    window.addEventListener('resize', scheduleMeasure, { passive: true });
    touchLayout.addEventListener('change', configureMotion);
    motion.addEventListener('change', configureMotion);
    document.fonts.ready.then(() => {
      if (!disposed) scheduleMeasure();
    });
    measure();
    configureMotion();

    return () => {
      disposed = true;
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(measureFrame);
      resize.disconnect();
      titleObserver?.disconnect();
      revealObserver?.disconnect();
      scroll.removeEventListener('scroll', scheduleDesktop);
      root.removeEventListener('focusin', onFocus);
      window.removeEventListener('resize', scheduleMeasure);
      touchLayout.removeEventListener('change', configureMotion);
      motion.removeEventListener('change', configureMotion);
    };
  }, [onCompact]);

  const scrollTo = (element: HTMLElement | null) => {
    const scroll = scrollRef.current;
    if (!scroll || !element) return;
    const toolbarHeight =
      rootRef.current?.querySelector('.pd-toolbar')?.getBoundingClientRect().height || 80;
    const top =
      element.getBoundingClientRect().top -
      scroll.getBoundingClientRect().top +
      scroll.scrollTop -
      toolbarHeight -
      20;
    scroll.scrollTo({
      top,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    });
  };

  return { rootRef, scrollRef, heroRef, titleRef, purchaseRef, scrollTo };
}
